import { InfiniteData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_CONSTANTS } from "../queryKeys";
import { UserNoteService } from "../../services/user-note.service";
import { IUserNote, IUserNotesPage } from "../../models/UserNote";

type UserNotesData = InfiniteData<IUserNotesPage, string | null>;

const userNotesKey = (userId: string) => [QUERY_CONSTANTS.GET_USER_NOTES, userId];

// "Show more" list: every fetchNextPage() appends the next page after the loaded ones
export const useGetUserNotesQuery = (userId: string) => {
  return useInfiniteQuery<IUserNotesPage, Error, UserNotesData, string[], string | null>({
    queryKey: userNotesKey(userId),
    queryFn: ({ pageParam }) => UserNoteService.getPage(userId, pageParam).then((res) => res.data),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
};

// Applies a create / edit / delete to the loaded pages, so the list keeps what is loaded instead of refetching every page.
// The pages' cursors stay valid - they point at a position in the list, not at an index.
export const useUserNotesCache = (userId: string) => {
  const queryClient = useQueryClient();

  const patch = (fn: (data: UserNotesData) => UserNotesData) =>
    queryClient.setQueryData<UserNotesData>(userNotesKey(userId), (data) =>
      data ? fn(data) : data
    );

  return {
    addNote: (note: IUserNote) =>
      patch((data) => ({
        ...data,
        pages: data.pages.map((page, i) => ({
          ...page,
          items: i === 0 ? [note, ...page.items] : page.items,
          total: page.total + 1,
        })),
      })),
    replaceNote: (note: IUserNote) =>
      patch((data) => ({
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => (item.id === note.id ? note : item)),
        })),
      })),
    removeNote: (id: string) =>
      patch((data) => ({
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.filter((item) => item.id !== id),
          total: page.total - 1,
        })),
      })),
  };
};
